import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Brand } from '../../types';
import { LinkBrandRedirectionCard } from './LinkBrandRedirectionCard';
import { useTranslation } from 'react-i18next';

interface BrandDropdownProps {
  brands: Brand[];
  selectedBrandId: string; // Empty string for "All Brands"
  onBrandSelect: (brandId: string) => void;
  className?: string;
  showRedirectionCard?: boolean;
  isLoading?: boolean;
  showAllBrandsOption?: boolean; // New prop to control visibility of "All Brands"
}

const ALL_BRANDS_ID = ''; // Constant for "All Brands" ID

export const BrandDropdown: React.FC<BrandDropdownProps> = ({
  brands = [],
  selectedBrandId, // Keep it as is, can be empty string for "All Brands"
  onBrandSelect,
  className = '',
  showRedirectionCard = true,
  isLoading = false,
  showAllBrandsOption = false, // Default to false, enable when needed
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  
  const validBrands = Array.isArray(brands) ? brands : [];
  const sortedBrands = useMemo(() => 
    [...validBrands].sort((a, b) => a.name.localeCompare(b.name))
  , [validBrands]);

  const allBrandsOption = useMemo(() => ({
    id: ALL_BRANDS_ID,
    name: t('brandDropdown.allBrands', 'All Brands'),
    logo: '' // Or a generic icon if you have one
  }), [t]);

  const dropdownOptions = useMemo(() => 
    showAllBrandsOption ? [allBrandsOption, ...sortedBrands] : sortedBrands
  , [showAllBrandsOption, allBrandsOption, sortedBrands]);

  const selectedOption = useMemo(() => {
    if (selectedBrandId === ALL_BRANDS_ID && showAllBrandsOption) {
      return allBrandsOption;
    }
    const brand = sortedBrands.find(b => b?.id === selectedBrandId);
    if (brand) return brand;
    if (sortedBrands.length > 0 && !showAllBrandsOption) return sortedBrands[0]; // Default to first if no specific selection and no "All Brands"
    if (sortedBrands.length > 0 && showAllBrandsOption && selectedBrandId !== ALL_BRANDS_ID && !selectedBrandId) return allBrandsOption; // Default to all brands if selectedBrandId is undefined/null and all brands is an option
    return {
      id: '',
      name: t('brandDropdown.noBrands', 'No brands available'),
      logo: ''
    };
  }, [selectedBrandId, showAllBrandsOption, allBrandsOption, sortedBrands, t]);

  const handleImageError = (brandId: string) => {
    setImageErrors(prev => ({
      ...prev,
      [brandId]: true
    }));
  };

  if (isLoading) {
    return (
      <div className={`min-w-60 flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg animate-pulse ${className}`}>
        <div className="w-5 h-5 rounded-full bg-gray-200"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <ChevronDown className="w-4 h-4 text-gray-300" />
      </div>
    );
  }

  if (validBrands.length === 0 && !showAllBrandsOption && showRedirectionCard) {
    return <LinkBrandRedirectionCard className={className} />;
  }

  if (dropdownOptions.length === 0 || (validBrands.length === 0 && !showAllBrandsOption) ){
    return (
      <div className={`min-w-60 flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-400 ${className}`}>
        <span className="text-sm max-w-[150px] truncate">{t('brandDropdown.noBrands', 'No brands available')}</span>
        <ChevronDown className="w-4 h-4 text-gray-300" />
      </div>
    );
  }
  
  return (
    <div className="relative min-w-60">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:text-primary-600 transition-colors ${className}`}
      >
        <span className="flex items-center gap-2 overflow-hidden">
          {selectedOption.logo && !imageErrors[selectedOption.id] && (
            <img 
              src={selectedOption.logo} 
              alt={selectedOption.name} 
              className="w-5 h-5 rounded-full flex-shrink-0"
              onError={() => handleImageError(selectedOption.id)}
            />
          )}
          <span className="text-sm text-gray-700 truncate">
            {selectedOption.name}
          </span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 right-0 mt-2 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1 max-h-60 overflow-y-auto"> {/* Added max-height and overflow for scroll */}
              {dropdownOptions.map(option => (
                <button
                  type="button"
                  key={option.id} // ALL_BRANDS_ID is empty string, which is fine for a key here
                  onClick={() => {
                    onBrandSelect(option.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm ${
                    option.id === selectedOption.id
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  {option.logo && !imageErrors[option.id] && (
                    <img 
                      src={option.logo} 
                      alt={option.name} 
                      className="w-5 h-5 rounded-full"
                      onError={() => handleImageError(option.id)}
                    />
                  )}
                  <span className="truncate">{option.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};